import type { FeatureFlagKeyType } from '../types/feature-flags'
import { FeatureFlagKey } from '../types/feature-flags'

/**
 * Maps a Help Center category (help_entry.module_slug) to the feature-flag KEY
 * that gates its availability. `null` means the category/article is always
 * visible (general guidance: getting started, account/admin basics, settings,
 * integrations, and organization guidance).
 *
 * Gating help by feature flag — not by isPersonal — means a personal account
 * (business flags OFF) never sees help for Tickets, Invoices, Time Tracking,
 * Deals, Products, Forms, Team, etc., AND any business org that disables a
 * module automatically loses its help too. This mirrors MODULE_TYPE_FEATURE_FLAG
 * (which hides permission categories the same way).
 *
 * The authoritative per-entry value lives in help_entry.feature_flag_key (set by
 * the seed / admin). This map is the fallback used to derive a category's flag
 * from its slug (e.g. for the category list, which only carries slug + count),
 * and to default admin-created entries.
 */
export const HELP_MODULE_FEATURE_FLAG: Record<string, string | null> = {
  // Always-visible / general
  'getting-started': null,
  admin: null,
  settings: null,
  integrations: null,
  'my-organization': null,
  // Agent accounts have no module flag: the surface is organization-only
  // (routes are orgOnly and the plan feature is inert for personal spaces), so
  // the category is hidden from personal accounts via HELP_WORK_ONLY_MODULES.
  agents: null,

  // Core personal + business modules (on in personal accounts)
  documents: FeatureFlagKey.DOCUMENTS.key,
  notes: FeatureFlagKey.NOTES.key,
  projects: FeatureFlagKey.PROJECTS.key,
  tasks: FeatureFlagKey.TASKS.key,
  'my-work': FeatureFlagKey.TASKS.key, // My Work is the personal task hub
  'task-templates': FeatureFlagKey.TASK_TEMPLATES.key,
  contacts: FeatureFlagKey.CONTACTS.key,
  enrichment: FeatureFlagKey.ENRICHMENT.key, // Contacts → Data quality
  'web-tracking': FeatureFlagKey.WEB_TRACKING.key, // Marketing → Tracking
  clients: FeatureFlagKey.ENTITIES.key, // "Clients" is the Entities/People module
  calendar: FeatureFlagKey.CALENDAR.key,
  events: FeatureFlagKey.EVENTS.key,
  collections: FeatureFlagKey.COLLECTIONS.key,
  suppliers: FeatureFlagKey.SUPPLIERS.key,
  benny: FeatureFlagKey.AI_FEATURES.key,

  // Business modules (off in the personal profile)
  marketing: FeatureFlagKey.MARKETING.key,
  scheduling: FeatureFlagKey.SCHEDULING.key,
  'live-chat': FeatureFlagKey.LIVE_CHAT.key,
  quotes: FeatureFlagKey.QUOTES.key,
  products: FeatureFlagKey.PRODUCTS.key, // also covers stock (the Inventory UI lives in Products)
  'knowledge-base': FeatureFlagKey.KNOWLEDGE_BASE.key,
  communications: FeatureFlagKey.CALLING.key,
  crm: FeatureFlagKey.DEALS.key,
  contracts: FeatureFlagKey.CONTRACTS.key,
  invoices: FeatureFlagKey.INVOICES.key,
  financials: FeatureFlagKey.FINANCIALS.key, // the Profitability module
  team: FeatureFlagKey.TEAM.key,
  tickets: FeatureFlagKey.TICKETS.key,
  time: FeatureFlagKey.TIME_TRACKING.key,
  expenses: FeatureFlagKey.TIME_TRACKING.key, // expenses ship with the time/expense surface
  forms: FeatureFlagKey.FORMS.key,
  focus: FeatureFlagKey.FOCUS_MODE.key,
  workstream: FeatureFlagKey.WORKSTREAM.key,
  workflows: FeatureFlagKey.WORKFLOWS.key,
  analytics: FeatureFlagKey.ADVANCED_REPORTS.key,
  discussions: FeatureFlagKey.DISCUSSIONS.key,
  deployments: FeatureFlagKey.PROJECTS.key, // deployments surface hangs off projects
}

/**
 * Categories whose product surface is organization-only but has no module
 * feature flag to gate on. Help clients hide these from personal (family)
 * spaces in addition to the flag check, because a personal owner passes every
 * permission check and the subscription layer is inert for personal accounts.
 */
export const HELP_WORK_ONLY_MODULES: ReadonlySet<string> = new Set(['agents'])

/** True when a help category is hidden in personal (family) spaces. */
export function isHelpModuleWorkOnly(
  moduleSlug: string | null | undefined,
): boolean {
  return !!moduleSlug && HELP_WORK_ONLY_MODULES.has(moduleSlug)
}

/**
 * Derive the gating feature-flag KEY for a help category slug. Returns `null`
 * for general categories (and unknown slugs — fail open to "always visible" so a
 * new, unmapped category is never hidden by accident).
 */
export function helpFeatureFlagForModule(
  moduleSlug: string | null | undefined,
): string | null {
  if (!moduleSlug) return null
  return HELP_MODULE_FEATURE_FLAG[moduleSlug] ?? null
}

/**
 * Resolve a feature-flag KEY string (e.g. "tickets", "time_tracking") back to
 * its FeatureFlagKeyType (e.g. "TICKETS", "TIME_TRACKING") so callers can look
 * it up in the flag map returned by useFeatureFlags. Returns `undefined` for an
 * unknown key.
 */
export function featureFlagKeyTypeFromKey(
  key: string | null | undefined,
): FeatureFlagKeyType | undefined {
  if (!key) return undefined
  return (Object.keys(FeatureFlagKey) as FeatureFlagKeyType[]).find(
    type => FeatureFlagKey[type].key === key,
  )
}
